import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { friendlyErrorMessage } from '../lib/errors';
import { Button } from '../components/Button';
import { TextField } from '../components/Field';

/**
 * Lands here from the Supabase "reset password" email link, which carries a
 * recovery token in the URL and fires a PASSWORD_RECOVERY auth event once
 * the client picks it up -- at that point there's a session, and the only
 * thing left is to set a new password.
 */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setReady(true);
    });
    // Also handle the case where the session is already set by the time this mounts.
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (updateError) {
      setError(friendlyErrorMessage(updateError));
    } else {
      setDone(true);
      setTimeout(() => navigate('/'), 1500);
    }
  }

  return (
    <div className="center-screen">
      <div className="card auth-card">
        <h1 style={{ fontSize: 24, marginBottom: 8 }}>Set a new password</h1>
        {!ready ? (
          <p className="muted">Open this page from the link in your password reset email.</p>
        ) : done ? (
          <p className="muted">Password updated. Taking you in…</p>
        ) : (
          <form onSubmit={handleSubmit}>
            <TextField
              label="New password"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error ? <p className="error" style={{ marginBottom: 12 }}>{error}</p> : null}
            <Button type="submit" disabled={busy} style={{ width: '100%' }}>
              {busy ? 'Saving…' : 'Save password'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
